import { ApiError } from "./error";
import type { ApiClientConfig, RequestOptions, UploadOptions } from "./types";

const REFRESH_LOCK = "safers-auth-refresh";
const REFRESHED_AT_KEY = "safers-auth-refreshed-at";

/** 마지막으로 갱신에 성공한 시각. 탭끼리 보도록 localStorage 에 둔다. */
function readRefreshedAt(): number {
  try {
    return Number(localStorage.getItem(REFRESHED_AT_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeRefreshedAt(time: number) {
  try {
    localStorage.setItem(REFRESHED_AT_KEY, String(time));
  } catch {
    // 못 남기면 다음 탭이 한 번 더 갱신할 뿐이다. 잠금 안이라 재사용은 생기지 않는다.
  }
}

export function createApiClient(config: ApiClientConfig) {
  const { baseURL, refreshTokenURL = "/auth/refresh-token", onUnauthorized } = config;

  let isRefreshing = false;
  let refreshPromise: Promise<boolean> | null = null;

  function buildURL(path: string, params?: RequestOptions["params"]): string {
    let fullPath = baseURL.startsWith("http")
      ? new URL(path, baseURL).toString()
      : `${baseURL}${path}`;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value === undefined) return;
        if (Array.isArray(value)) {
          value.forEach((v) => v !== undefined && searchParams.append(key, String(v)));
          return;
        }
        searchParams.set(key, String(value));
      });
      const queryString = searchParams.toString();
      if (queryString) {
        fullPath += `?${queryString}`;
      }
    }

    return fullPath;
  }

  /**
   * - `refreshed`: 새 토큰을 받았다.
   * - `rejected`: 세션이 없다(4xx — 토큰 만료·폐기, 계정 삭제). 로그아웃한다.
   * - `unavailable`: 네트워크 오류·5xx. 세션은 살아 있을 수 있으니 로그아웃하지 않는다.
   *   배포·재시작과 갱신 시점이 겹쳐도 켜 둔 관제 화면이 로그인으로 넘어가지 않게 한다.
   */
  type RefreshResult = "refreshed" | "rejected" | "unavailable";

  async function refreshToken(): Promise<RefreshResult> {
    try {
      const response = await fetch(buildURL(refreshTokenURL), {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) return "refreshed";
      return response.status < 500 ? "rejected" : "unavailable";
    } catch {
      return "unavailable";
    }
  }

  /**
   * 탭끼리도 갱신을 하나씩 한다. 같은 브라우저의 탭(관제 여러 개, `/admin`)은 리프레시
   * 쿠키를 같이 쓴다. 서버는 갱신마다 토큰을 바꾸고 바뀌기 전 토큰이 다시 오면 세션을
   * 지우므로, 두 탭이 같은 토큰으로 동시에 갱신하면 모든 탭이 로그아웃된다.
   *
   * 잠금 안에서 보내는 요청은 늘 직전 갱신이 내려준 쿠키를 실으니 재사용이 생기지 않는다.
   * 기다리는 사이 다른 탭이 갱신했다면 다시 갱신하지 않는다.
   * Web Locks 는 보안 컨텍스트(https·localhost)에만 있다. 없으면 탭 안에서만 묶는다.
   */
  async function refreshAcrossTabs(): Promise<RefreshResult> {
    if (typeof navigator === "undefined" || !navigator.locks) {
      return refreshToken();
    }
    const requestedAt = Date.now();
    return navigator.locks.request(REFRESH_LOCK, async () => {
      if (readRefreshedAt() > requestedAt) return "refreshed";
      const result = await refreshToken();
      if (result === "refreshed") writeRefreshedAt(Date.now());
      return result;
    });
  }

  async function handleUnauthorized(): Promise<boolean> {
    if (isRefreshing && refreshPromise) {
      return refreshPromise;
    }

    isRefreshing = true;
    refreshPromise = refreshAcrossTabs().then((result) => {
      if (result === "rejected" && onUnauthorized) {
        onUnauthorized();
      }
      return result === "refreshed";
    });

    const success = await refreshPromise;

    isRefreshing = false;
    refreshPromise = null;

    return success;
  }

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    options: RequestOptions = {},
    isRetry = false
  ): Promise<T> {
    const { params, headers: customHeaders, auth = "session", ...restOptions } = options;

    const url = buildURL(path, params);
    const isFormData = body instanceof FormData;

    const headers: HeadersInit = {
      ...(body !== undefined && !isFormData && { "Content-Type": "application/json" }),
      ...customHeaders,
    };

    const response = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? (isFormData ? body : JSON.stringify(body)) : undefined,
      credentials: auth === "public" ? "same-origin" : "include",
      ...restOptions,
    });

    if (response.status === 401 && auth === "session" && !isRetry) {
      const refreshed = await handleUnauthorized();
      if (refreshed) {
        return request<T>(method, path, body, options, true);
      }
    }

    if (response.status === 204) {
      return undefined as T;
    }

    if (!response.ok) {
      throw await ApiError.fromResponse(response);
    }

    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      const data = await response.json();
      if (response.status === 201) {
        const location = response.headers.get("Location");
        // 본문이 객체가 아닐 수 있다 — 점검표 등록은 봉투 없는 문자열을 준다.
        // 그대로 펼치면 `{0:"2",1:"0",…}` 가 되어 값이 사라진다.
        return (
          data !== null && typeof data === "object" ? { ...data, location } : { data, location }
        ) as T;
      }
      return data as T;
    }

    if (response.status === 201) {
      const location = response.headers.get("Location");
      return { location } as T;
    }

    return undefined as T;
  }

  return {
    /**
     * 몸통을 파싱하지 않고 `Response` 를 넘긴다. 스트림으로 읽어야 하는 응답이 쓴다.
     *
     * 401 재시도를 붙여도 안전하다 — 401 이 온 시점엔 아직 `body` 를 읽지 않았다.
     * 읽기 시작한 뒤에는 되감을 수 없으니, 재시도는 여기서 한 번만 한다.
     */
    async stream(path: string, options: RequestOptions = {}, isRetry = false): Promise<Response> {
      const { params, headers, auth = "session", ...restOptions } = options;

      const response = await fetch(buildURL(path, params), {
        headers,
        credentials: auth === "public" ? "same-origin" : "include",
        ...restOptions,
      });

      if (response.status === 401 && auth === "session" && !isRetry) {
        const refreshed = await handleUnauthorized();
        if (refreshed) {
          return this.stream(path, options, true);
        }
      }

      if (!response.ok) {
        throw await ApiError.fromResponse(response);
      }

      return response;
    },

    get<T>(path: string, options?: RequestOptions): Promise<T> {
      return request<T>("GET", path, undefined, options);
    },

    post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
      return request<T>("POST", path, body, options);
    },

    put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
      return request<T>("PUT", path, body, options);
    },

    patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
      return request<T>("PATCH", path, body, options);
    },

    /** 2번째 인자는 request body (일괄 삭제 등) — options 는 3번째 인자. put/patch 와 동일한 시그니처. */
    delete<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
      return request<T>("DELETE", path, body, options);
    },

    upload<T>(path: string, file: File | File[], options: UploadOptions = {}): Promise<T> {
      const { fieldName = "file", onProgress, headers: customHeaders } = options;

      const executeUpload = (isRetry = false): Promise<T> => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          const url = buildURL(path);

          const formData = new FormData();
          if (Array.isArray(file)) {
            file.forEach((f) => formData.append(fieldName, f));
          } else {
            formData.append(fieldName, file);
          }

          if (onProgress) {
            xhr.upload.addEventListener("progress", (event) => {
              if (event.lengthComputable) {
                const progress = Math.round((event.loaded / event.total) * 100);
                onProgress(progress);
              }
            });
          }

          xhr.addEventListener("load", async () => {
            if (xhr.status === 401 && !isRetry) {
              const refreshed = await handleUnauthorized();
              if (refreshed) {
                resolve(executeUpload(true));
                return;
              }
            }

            if (xhr.status >= 200 && xhr.status < 300) {
              const text = xhr.responseText;
              const location = xhr.status === 201 ? xhr.getResponseHeader("Location") : null;

              if (xhr.status === 204 || !text) {
                resolve((location ? { location } : undefined) as T);
                return;
              }

              try {
                const responseData = JSON.parse(text);
                resolve((location !== null ? { ...responseData, location } : responseData) as T);
              } catch {
                reject(new ApiError(xhr.status, "Failed to parse JSON response", "PARSE_ERROR"));
              }
            } else {
              let errorResponse;
              try {
                errorResponse = JSON.parse(xhr.responseText);
              } catch {
                // ignore
              }
              reject(
                new ApiError(
                  xhr.status,
                  errorResponse?.message || xhr.statusText,
                  errorResponse?.code,
                  errorResponse
                )
              );
            }
          });

          xhr.addEventListener("error", () => {
            reject(new ApiError(0, "Network error", "NETWORK_ERROR"));
          });

          xhr.open("POST", url);
          xhr.withCredentials = true;

          if (customHeaders) {
            Object.entries(customHeaders).forEach(([key, value]) => {
              if (typeof value === "string") {
                xhr.setRequestHeader(key, value);
              }
            });
          }

          xhr.send(formData);
        });
      };

      return executeUpload();
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

let client: ApiClient | null = null;

export const configureApi = (config: ApiClientConfig) => {
  client = createApiClient(config);
};

export const getApiClient = (): ApiClient => {
  if (!client) {
    throw new Error("API client not configured. Call configureApi() first.");
  }
  return client;
};
